<script setup>
/**
 * [evotek P30 B2 + P31 B3] BrandSettings.vue
 *
 * Brand settings form for account administrators.
 * Rendered only when feature flag `brand_customization` is enabled for the account.
 *
 * P30 B2 features:
 *   - Color picker (native <input type="color"> + hex text input)
 *   - Logo URL text field (manual, backward-compat)
 *   - Live preview box: button, sidebar icon swatch, link sample
 *   - WCAG guard real-time: shows warning + derived buttonText rule + contrast ratios
 *   - Save: PATCH /api/v1/accounts/:id with { brand_theme: { brandColor, logoUrl } }
 *   - Reset: PATCH brand_theme: null → clears to default
 *
 * P31 B3 additions:
 *   - File upload: <input type="file"> accepts png/jpeg/svg
 *   - Client-side validation: type, size (≤512 KB), optional dimension display
 *   - Upload to POST /api/v1/accounts/:id/upload_brand_logo (multipart)
 *   - Delete: DELETE /api/v1/accounts/:id/brand_logo
 *   - Preview: shows uploaded logo thumbnail (account.brand_logo_url)
 *   - After upload/delete: dispatch accounts/get to refresh store → Logo.vue re-renders
 *   - re-calls setBrandTheme with updated brandLogoUrl so CSS logo rule also refreshes
 *   - UX: file input + upload + delete grouped above legacy URL field
 *     Legacy URL field kept for backward compat ("or enter URL manually")
 *
 * Permission: only account administrator role, only when flag is enabled.
 * Role check: Vuex `auth/getCurrentRole` (role is per-account in evochat/Chatwoot).
 * Flag check: `currentAccount.custom_attributes.brand_customization_enabled`.
 */
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { setBrandTheme, sanitizeLogoUrl } from 'dashboard/helper/brandTheme';
import {
  deriveBrandTheme,
  contrastRatio,
  relativeLuminance,
  parseHex,
} from 'dashboard/helper/brandColorDerive';
import SectionLayout from './SectionLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'next/input/Input.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';

const { t } = useI18n();
const store = useStore();
const { currentAccount, accountId, updateAccount } = useAccount();

// ── reactive form state ──────────────────────────────────────────────────────

const brandColor = ref('#2781F6');
const logoUrl = ref('');
const isSaving = ref(false);
const isResetting = ref(false);

// [P31 B3] File upload state
const logoFile = ref(null);           // selected File object
const logoPreviewDataUrl = ref(null); // local preview (before upload)
const isUploading = ref(false);
const isDeleting = ref(false);
const uploadFileError = ref('');      // client-side file validation error

// ── initialise from account ──────────────────────────────────────────────────

watch(
  currentAccount,
  account => {
    if (!account) return;
    const bt = account.custom_attributes?.brand_theme;
    if (bt) {
      brandColor.value = bt.brandColor || '#2781F6';
      logoUrl.value = bt.logoUrl || '';
    } else {
      brandColor.value = '#2781F6';
      logoUrl.value = '';
    }
    // Clear pending file selection when account reloads (e.g. after upload)
    logoFile.value = null;
    logoPreviewDataUrl.value = null;
    uploadFileError.value = '';
  },
  { immediate: true, deep: true }
);

// ── visibility / permission guard ────────────────────────────────────────────

// [P30 fix] brand_customization_enabled now lives in custom_attributes (not features).
// Moved from FlagShihTzu due to bigint overflow at feature #64.
const isFeatureEnabled = computed(() => {
  const attrs = currentAccount.value?.custom_attributes;
  if (!attrs || typeof attrs !== 'object') return false;
  return attrs.brand_customization_enabled === true;
});

// [P30 fix] Role in evochat/Chatwoot is per-account. Use canonical getCurrentRole getter.
// currentUser.role does not exist at top level — role is in accounts[].role.
const isAdministrator = computed(
  () => store.getters['auth/getCurrentRole'] === 'administrator'
);

const canEdit = computed(() => isFeatureEnabled.value && isAdministrator.value);

// ── computed: current uploaded logo URL (ActiveStorage) ──────────────────────

const currentLogoUrl = computed(() => currentAccount.value?.brand_logo_url || null);

// ── live derived tokens (real-time from brandColor) ──────────────────────────

const derived = computed(() => {
  try {
    return deriveBrandTheme(brandColor.value);
  } catch {
    return null;
  }
});

/**
 * WCAG contrast of white (#fff) against the current brandColor.
 */
const whiteOnBrandContrast = computed(() => {
  if (!derived.value) return null;
  const { r, g, b } = parseHex(brandColor.value);
  const brandLum = relativeLuminance(r, g, b);
  return contrastRatio(1.0, brandLum);
});

const willUseDarkButtonText = computed(
  () => derived.value?.buttonTextRule != null
);

const blue11LightContrast = computed(() => {
  if (!derived.value) return null;
  const [rS, gS, bS] = derived.value.blue11Light.split(' ').map(Number);
  const lum = relativeLuminance(rS, gS, bS);
  return contrastRatio(lum, 0.9808);
});

const blue11DarkContrast = computed(() => {
  if (!derived.value) return null;
  const [rS, gS, bS] = derived.value.blue11Dark.split(' ').map(Number);
  const lum = relativeLuminance(rS, gS, bS);
  return contrastRatio(lum, 0.0095);
});

// ── preview computed styles ──────────────────────────────────────────────────

const previewButtonStyle = computed(() => ({
  backgroundColor: brandColor.value,
  color: willUseDarkButtonText.value ? '#1c2024' : '#ffffff',
  borderRadius: '6px',
  padding: '6px 14px',
  fontSize: '13px',
  fontWeight: '600',
  border: 'none',
  cursor: 'default',
  display: 'inline-block',
}));

const previewIconStyle = computed(() => ({
  width: '20px',
  height: '20px',
  borderRadius: '4px',
  backgroundColor: brandColor.value,
  opacity: '0.65',
  display: 'inline-block',
  verticalAlign: 'middle',
}));

const previewLinkStyle = computed(() => ({
  color: brandColor.value,
  fontWeight: '500',
  fontSize: '13px',
  textDecoration: 'underline',
}));

// ── hex validation ────────────────────────────────────────────────────────────

const isValidHex = computed(() => /^#[0-9a-fA-F]{6}$/.test(brandColor.value));

/**
 * [P30 M1 / P31 B3 updated] Client-side validation for logoUrl (manual URL).
 * Empty/blank is valid (optional). Non-empty must pass sanitizeLogoUrl guard.
 * [P31] sanitizeLogoUrl now also accepts /rails/active_storage/ prefix.
 */
const isValidLogoUrl = computed(() => {
  if (!logoUrl.value) return true;
  return sanitizeLogoUrl(logoUrl.value) !== null;
});

const isFormValid = computed(() => isValidHex.value && isValidLogoUrl.value);

// ── [P31 B3] File input constants ────────────────────────────────────────────

const MAX_FILE_SIZE_BYTES = 512 * 1024; // 512 KB
const ALLOWED_FILE_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml'];

// ── color picker ↔ text field sync ───────────────────────────────────────────

function onPickerChange(event) {
  brandColor.value = event.target.value.toUpperCase();
}

function onHexInput(value) {
  const v = value.startsWith('#') ? value : `#${value}`;
  brandColor.value = v;
}

// ── [P31 B3] File selection handler ──────────────────────────────────────────

function onFileSelected(event) {
  uploadFileError.value = '';
  const file = event.target.files?.[0];
  if (!file) {
    logoFile.value = null;
    logoPreviewDataUrl.value = null;
    return;
  }

  // Client-side type check
  if (!ALLOWED_FILE_TYPES.includes(file.type)) {
    uploadFileError.value = t('GENERAL_SETTINGS.FORM.BRAND.LOGO_UPLOAD.ERROR_TYPE');
    logoFile.value = null;
    logoPreviewDataUrl.value = null;
    // Reset the input so same file can be re-selected after fixing
    event.target.value = '';
    return;
  }

  // Client-side size check
  if (file.size > MAX_FILE_SIZE_BYTES) {
    uploadFileError.value = t('GENERAL_SETTINGS.FORM.BRAND.LOGO_UPLOAD.ERROR_SIZE', {
      size: (file.size / 1024).toFixed(1),
    });
    logoFile.value = null;
    logoPreviewDataUrl.value = null;
    event.target.value = '';
    return;
  }

  logoFile.value = file;

  // Generate local preview DataURL (for display before upload)
  const reader = new FileReader();
  reader.onload = e => {
    logoPreviewDataUrl.value = e.target.result;
  };
  reader.readAsDataURL(file);
}

// ── [P31 B3] Upload logo ──────────────────────────────────────────────────────

async function uploadLogo() {
  if (!logoFile.value) return;
  isUploading.value = true;
  uploadFileError.value = '';

  try {
    const formData = new FormData();
    formData.append('logo', logoFile.value);

    // POST multipart — axios imported at top of <script setup>.
    // axios automatically sets Content-Type: multipart/form-data with boundary.
    // api_access_token header is set globally by the axios interceptor (auth module).
    const response = await axios.post(
      `/api/v1/accounts/${accountId.value}/upload_brand_logo`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );

    // Refresh account in store to get updated brand_logo_url
    await store.dispatch('accounts/get');

    // Re-apply brand theme with new logo URL
    const account = currentAccount.value;
    const customAttrs = account?.custom_attributes || {};
    const brandThemeObj = customAttrs.brand_theme || null;
    const newLogoUrl = response.data?.brand_logo_url || account?.brand_logo_url || null;
    setBrandTheme(accountId.value, brandThemeObj, customAttrs, newLogoUrl);

    // Clear file selection
    logoFile.value = null;
    logoPreviewDataUrl.value = null;

    useAlert(t('GENERAL_SETTINGS.FORM.BRAND.LOGO_UPLOAD.SUCCESS'));
  } catch (error) {
    const status = error?.response?.status;
    const msg = error?.response?.data?.error;
    if (status === 403) {
      useAlert(t('GENERAL_SETTINGS.FORM.BRAND.API.ERROR_FORBIDDEN'));
    } else if (status === 422 && msg) {
      useAlert(msg);
    } else {
      useAlert(t('GENERAL_SETTINGS.FORM.BRAND.LOGO_UPLOAD.ERROR_SERVER'));
    }
  } finally {
    isUploading.value = false;
  }
}

// ── [P31 B3] Delete logo ──────────────────────────────────────────────────────

async function deleteLogo() {
  isDeleting.value = true;
  try {
    // DELETE — axios imported at top of <script setup>.
    await axios.delete(`/api/v1/accounts/${accountId.value}/brand_logo`);

    // Refresh account in store
    await store.dispatch('accounts/get');

    // Re-apply brand theme without logo
    const account = currentAccount.value;
    const customAttrs = account?.custom_attributes || {};
    const brandThemeObj = customAttrs.brand_theme || null;
    setBrandTheme(accountId.value, brandThemeObj, customAttrs, null);

    useAlert(t('GENERAL_SETTINGS.FORM.BRAND.LOGO_UPLOAD.DELETE_SUCCESS'));
  } catch (error) {
    const status = error?.response?.status;
    if (status === 403) {
      useAlert(t('GENERAL_SETTINGS.FORM.BRAND.API.ERROR_FORBIDDEN'));
    } else {
      useAlert(t('GENERAL_SETTINGS.FORM.BRAND.LOGO_UPLOAD.ERROR_DELETE'));
    }
  } finally {
    isDeleting.value = false;
  }
}

// ── save brand theme ──────────────────────────────────────────────────────────

async function saveBrandTheme() {
  if (!isValidHex.value) {
    useAlert(t('GENERAL_SETTINGS.FORM.BRAND.VALIDATION.INVALID_COLOR'));
    return;
  }
  if (!isValidLogoUrl.value) {
    useAlert(t('GENERAL_SETTINGS.FORM.BRAND.VALIDATION.INVALID_LOGO_URL'));
    return;
  }
  isSaving.value = true;
  try {
    await updateAccount({
      brand_theme: {
        brandColor: brandColor.value,
        logoUrl: logoUrl.value || null,
      },
    });
    // Re-apply theme immediately (also pass current brand_logo_url for logo priority)
    const account = currentAccount.value;
    const customAttrs = account?.custom_attributes || {};
    const newLogoUrl = account?.brand_logo_url || null;
    setBrandTheme(
      accountId.value,
      { brandColor: brandColor.value, logoUrl: logoUrl.value || null },
      customAttrs,
      newLogoUrl
    );
    useAlert(t('GENERAL_SETTINGS.FORM.BRAND.API.SUCCESS'));
  } catch (error) {
    const status = error?.response?.status;
    if (status === 403) {
      useAlert(t('GENERAL_SETTINGS.FORM.BRAND.API.ERROR_FORBIDDEN'));
    } else if (status === 422) {
      const msg =
        error?.response?.data?.error ||
        t('GENERAL_SETTINGS.FORM.BRAND.API.ERROR_VALIDATION');
      useAlert(msg);
    } else {
      useAlert(t('GENERAL_SETTINGS.FORM.BRAND.API.ERROR'));
    }
  } finally {
    isSaving.value = false;
  }
}

// ── reset to default ──────────────────────────────────────────────────────────

async function resetBrandTheme() {
  isResetting.value = true;
  try {
    await updateAccount({ brand_theme: null });
    brandColor.value = '#2781F6';
    logoUrl.value = '';
    setBrandTheme(accountId.value, null, currentAccount.value?.custom_attributes || {}, null);
    useAlert(t('GENERAL_SETTINGS.FORM.BRAND.API.RESET_SUCCESS'));
  } catch {
    useAlert(t('GENERAL_SETTINGS.FORM.BRAND.API.ERROR'));
  } finally {
    isResetting.value = false;
  }
}
</script>

<template>
  <!-- Only render section for administrators when flag is enabled -->
  <SectionLayout
    v-if="isAdministrator"
    :title="t('GENERAL_SETTINGS.FORM.BRAND.TITLE')"
    :description="t('GENERAL_SETTINGS.FORM.BRAND.NOTE')"
    with-border
  >
    <!-- Flag disabled: show read-only note -->
    <template v-if="!isFeatureEnabled">
      <p class="text-sm text-n-slate-11 italic">
        {{ t('GENERAL_SETTINGS.FORM.BRAND.FLAG_DISABLED_NOTE') }}
      </p>
    </template>

    <!-- Flag enabled: show full form -->
    <template v-else>
      <div class="grid gap-5">
        <!-- Brand color picker -->
        <WithLabel
          name="brand-color"
          :label="t('GENERAL_SETTINGS.FORM.BRAND.COLOR.LABEL')"
        >
          <div class="flex items-center gap-3">
            <input
              type="color"
              :value="brandColor"
              class="w-10 h-10 rounded cursor-pointer border border-n-weak p-0.5 bg-transparent"
              :title="t('GENERAL_SETTINGS.FORM.BRAND.COLOR.PICKER_TITLE')"
              :disabled="!canEdit"
              @input="onPickerChange"
            />
            <NextInput
              :model-value="brandColor"
              type="text"
              class="w-32 font-mono"
              :placeholder="t('GENERAL_SETTINGS.FORM.BRAND.COLOR.PLACEHOLDER')"
              :disabled="!canEdit"
              @update:model-value="onHexInput"
            />
            <span
              class="inline-block w-6 h-6 rounded border border-n-weak"
              :style="{ backgroundColor: brandColor }"
              :title="brandColor"
            />
          </div>
          <template v-if="!isValidHex" #help>
            <span class="text-n-ruby-11">
              {{ t('GENERAL_SETTINGS.FORM.BRAND.VALIDATION.INVALID_COLOR') }}
            </span>
          </template>
        </WithLabel>

        <!-- [P31 B3] Logo upload section -->
        <WithLabel
          name="brand-logo-upload"
          :label="t('GENERAL_SETTINGS.FORM.BRAND.LOGO_UPLOAD.LABEL')"
        >
          <!-- Current uploaded logo preview (from ActiveStorage) -->
          <div v-if="currentLogoUrl" class="mb-3 flex items-center gap-3">
            <img
              :src="currentLogoUrl"
              :alt="t('GENERAL_SETTINGS.FORM.BRAND.LOGO_UPLOAD.CURRENT_ALT')"
              class="h-10 w-auto max-w-[120px] rounded border border-n-weak object-contain bg-n-slate-2 p-1"
            />
            <span class="text-xs text-n-slate-11">
              {{ t('GENERAL_SETTINGS.FORM.BRAND.LOGO_UPLOAD.CURRENT_LABEL') }}
            </span>
            <NextButton
              size="small"
              :is-loading="isDeleting"
              :disabled="!canEdit || isDeleting || isUploading"
              @click="deleteLogo"
            >
              {{ t('GENERAL_SETTINGS.FORM.BRAND.LOGO_UPLOAD.DELETE') }}
            </NextButton>
          </div>

          <!-- Local preview (before upload) -->
          <div v-if="logoPreviewDataUrl && !currentLogoUrl" class="mb-3 flex items-center gap-3">
            <img
              :src="logoPreviewDataUrl"
              :alt="t('GENERAL_SETTINGS.FORM.BRAND.LOGO_UPLOAD.PREVIEW_ALT')"
              class="h-10 w-auto max-w-[120px] rounded border border-n-weak object-contain bg-n-slate-2 p-1"
            />
            <span class="text-xs text-n-slate-11">
              {{ t('GENERAL_SETTINGS.FORM.BRAND.LOGO_UPLOAD.PREVIEW_LABEL') }}
            </span>
          </div>

          <!-- File input + upload button row -->
          <div class="flex items-center gap-3 flex-wrap">
            <input
              type="file"
              accept="image/png,image/jpeg,image/svg+xml"
              :disabled="!canEdit || isUploading || isDeleting"
              class="text-sm text-n-slate-12 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-n-brand/10 file:text-n-brand hover:file:bg-n-brand/20 disabled:opacity-50"
              @change="onFileSelected"
            />
            <NextButton
              blue
              size="small"
              :is-loading="isUploading"
              :disabled="!canEdit || !logoFile || isUploading || isDeleting || !!uploadFileError"
              @click="uploadLogo"
            >
              {{ t('GENERAL_SETTINGS.FORM.BRAND.LOGO_UPLOAD.UPLOAD_BTN') }}
            </NextButton>
          </div>

          <!-- File validation error -->
          <template v-if="uploadFileError" #help>
            <span class="text-n-ruby-11">{{ uploadFileError }}</span>
          </template>
          <template v-else #help>
            {{ t('GENERAL_SETTINGS.FORM.BRAND.LOGO_UPLOAD.HELP') }}
          </template>
        </WithLabel>

        <!-- Legacy Logo URL (manual path, backward-compat) -->
        <WithLabel
          name="brand-logo-url"
          :label="t('GENERAL_SETTINGS.FORM.BRAND.LOGO_URL.LABEL')"
        >
          <NextInput
            v-model="logoUrl"
            type="text"
            class="w-full"
            :placeholder="t('GENERAL_SETTINGS.FORM.BRAND.LOGO_URL.PLACEHOLDER')"
            :disabled="!canEdit"
          />
          <template v-if="logoUrl && !isValidLogoUrl" #help>
            <span class="text-n-ruby-11">
              {{ t('GENERAL_SETTINGS.FORM.BRAND.VALIDATION.INVALID_LOGO_URL') }}
            </span>
          </template>
          <template v-else #help>
            {{ t('GENERAL_SETTINGS.FORM.BRAND.LOGO_URL.HELP') }}
          </template>
        </WithLabel>

        <!-- Live preview box -->
        <div
          v-if="isValidHex && derived"
          class="rounded-lg border border-n-weak bg-n-slate-2 p-4"
        >
          <p class="text-xs text-n-slate-11 font-semibold uppercase mb-3">
            {{ t('GENERAL_SETTINGS.FORM.BRAND.PREVIEW.TITLE') }}
          </p>
          <div class="flex flex-wrap items-center gap-4">
            <div>
              <p class="text-xs text-n-slate-11 mb-1">
                {{ t('GENERAL_SETTINGS.FORM.BRAND.PREVIEW.BUTTON') }}
              </p>
              <span :style="previewButtonStyle">
                {{ t('GENERAL_SETTINGS.FORM.BRAND.PREVIEW.SEND') }}
              </span>
            </div>
            <div>
              <p class="text-xs text-n-slate-11 mb-1">
                {{ t('GENERAL_SETTINGS.FORM.BRAND.PREVIEW.ICON') }}
              </p>
              <span :style="previewIconStyle" />
            </div>
            <div>
              <p class="text-xs text-n-slate-11 mb-1">
                {{ t('GENERAL_SETTINGS.FORM.BRAND.PREVIEW.LINK') }}
              </p>
              <a href="#" :style="previewLinkStyle" @click.prevent>
                {{ t('GENERAL_SETTINGS.FORM.BRAND.PREVIEW.LINK_SAMPLE') }}
              </a>
            </div>
          </div>

          <div class="mt-3 grid grid-cols-2 gap-2 text-xs text-n-slate-11">
            <span>
              {{ t('GENERAL_SETTINGS.FORM.BRAND.PREVIEW.CONTRAST_LINK_LIGHT') }}
              <strong class="text-n-slate-12">
                {{ blue11LightContrast?.toFixed(2) }}:1
              </strong>
            </span>
            <span>
              {{ t('GENERAL_SETTINGS.FORM.BRAND.PREVIEW.CONTRAST_LINK_DARK') }}
              <strong class="text-n-slate-12">
                {{ blue11DarkContrast?.toFixed(2) }}:1
              </strong>
            </span>
            <span>
              {{ t('GENERAL_SETTINGS.FORM.BRAND.PREVIEW.CONTRAST_BTN') }}
              <strong class="text-n-slate-12">
                {{ whiteOnBrandContrast?.toFixed(2) }}:1
              </strong>
            </span>
          </div>
        </div>

        <!-- WCAG warning banner -->
        <div
          v-if="isValidHex && willUseDarkButtonText"
          class="flex items-start gap-2 rounded-lg border border-n-amber-7 bg-n-amber-3 px-3 py-2 text-sm text-n-amber-12"
          role="alert"
        >
          <span class="i-lucide-triangle-alert mt-0.5 shrink-0 text-n-amber-11" />
          <span>{{ t('GENERAL_SETTINGS.FORM.BRAND.WCAG_WARNING', { ratio: whiteOnBrandContrast?.toFixed(2) }) }}</span>
        </div>

        <!-- Action buttons -->
        <div class="flex items-center gap-3">
          <NextButton
            blue
            :is-loading="isSaving"
            :disabled="!canEdit || !isFormValid || isSaving || isResetting"
            @click="saveBrandTheme"
          >
            {{ t('GENERAL_SETTINGS.FORM.BRAND.SAVE') }}
          </NextButton>
          <NextButton
            :is-loading="isResetting"
            :disabled="!canEdit || isSaving || isResetting"
            @click="resetBrandTheme"
          >
            {{ t('GENERAL_SETTINGS.FORM.BRAND.RESET') }}
          </NextButton>
        </div>
      </div>
    </template>
  </SectionLayout>
</template>
